import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "src/shared/config/storeConfig";
import { clearAuthReturnTo } from "src/shared/utilities/authRedirect";
import {
  RegisterFormParams,
  UpdateUserAccountPayloads,
} from "src/shared/models/containers/account.model";
import {
  MoveInConditionFormSubmitPayload,
  MoveInConditionStatusResponse,
  MoveInConditionSubmitResponse,
} from "src/shared/models/containers/moveInCondition.model";

export type RebindLeaseOption = {
  personId: string;
  personName: string;
  leaseId: string;
  status: string;
  unit: string;
  propertyName: string;
  current: boolean;
};

export const privateApi = createApi({
  reducerPath: "privateApi",
  tagTypes: [
    "Account",
    "Requests",
    "PaymentMethods",
    "Balance",
    "AutoPay",
    "Reporting",
    "WoCategories",
    "MoveInCondition",
  ],
  baseQuery: prepareBaseQuery(false),
  endpoints: (builder) => ({
    resendEmail: builder.mutation({
      query: () => ({
        url: `v1/users/send-verify-email`,
        method: "POST",
      }),
    }),
    resetPasswordEmail: builder.mutation({
      query: () => ({
        url: `v1/users/reset-password-email`,
        method: "POST",
      }),
    }),
    getUser: builder.query({
      query: () => ({
        url: `/v1/users/`,
        method: "GET",
      }),
      providesTags: ["Reporting", "AutoPay", "PaymentMethods", "Account"],
    }),
    // Short-lived signed token identifying the resident's property + unit, handed
    // to the embedded Rex Support OS chatbot so it can act on the resident's
    // GetDone work orders.
    getSupportContext: builder.query<{ token: string }, void>({
      query: () => ({
        url: `/v1/support-context`,
        method: "GET",
      }),
      // The token expires. Never serve a cached one to a new mount, or the
      // chatbot starts the conversation holding something already dead.
      keepUnusedDataFor: 0,
    }),
    createUser: builder.mutation({
      query: (userDataBody: RegisterFormParams) => ({
        url: `/v1/users/`,
        method: "POST",
        body: userDataBody,
      }),
    }),
    updateUserAccount: builder.mutation({
      query: (userDataBody: UpdateUserAccountPayloads) => ({
        url: `/v1/users/update-account`,
        method: "POST",
        body: userDataBody,
      }),
      invalidatesTags: ["Reporting", "AutoPay", "PaymentMethods", "Account"],
    }),
    getRebindLeases: builder.query<
      { current: RebindLeaseOption | null; leases: RebindLeaseOption[] },
      void
    >({
      query: () => ({
        url: `/v1/users/rebind-leases`,
        method: "GET",
      }),
      providesTags: ["Account"],
    }),
    rebindLease: builder.mutation<
      { noop: boolean },
      { targetPersonId: string }
    >({
      query: ({ targetPersonId }) => ({
        url: `/v1/users/rebind-leases`,
        method: "POST",
        body: { targetPersonId, confirm: true },
      }),
      invalidatesTags: ["Account", "PaymentMethods", "Balance", "AutoPay"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          clearAuthReturnTo();
        } catch {
          // The confirm dialog surfaces the error. Leave any saved return path.
        }
      },
    }),
    getWorkOrderCategories: builder.mutation({
      query: () => ({
        url: `/v2/work-orders/categories`,
        method: "GET",
      }),
    }),
    getWorkOrderCategoriesQ: builder.query({
      query: () => ({
        url: `/v2/work-orders/categories`,
        method: "GET",
      }),
      providesTags: ["WoCategories"],
    }),
    createWorkOrder: builder.mutation({
      query: (body) => ({
        url: `/v1/work-orders`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Requests"],
    }),
    getWorkOrders: builder.query({
      query: (obj: any) => {
        if (obj.isDateFilterAvailable) {
          return {
            url: `/v1/work-orders`,
            method: "GET",
            params: obj.params,
          };
        }
        return {
          url: `/v1/work-orders`,
          method: "GET",
        };
      },
      providesTags: ["Requests"],
    }),
    getWorkOrderById: builder.query({
      query: (id: string) => {
        return {
          url: `/v1/work-orders/by-id/${id}`,
          method: "GET",
        };
      },
      providesTags: ["Requests"],
    }),
    getPaymentMethods: builder.query({
      query: () => {
        return {
          url: `/v1/payments/methods`,
          method: "GET",
        };
      },
      providesTags: ["PaymentMethods"],
    }),
    addBankAccount: builder.mutation({
      query: (body) => ({
        url: `/v1/payments/methods/add-bank`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    updateDefaultBankAccount: builder.mutation({
      query: (state) => ({
        url: `/v1/payments/methods/${state.id}/set-default`,
        method: "PATCH",
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    deleteBankAccount: builder.mutation({
      query: (state) => ({
        url: `/v1/payments/methods/${state.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    getBalanceDetails: builder.query({
      query: () => {
        return {
          url: `/v1/users/account-balance`,
          method: "GET",
        };
      },
      providesTags: ["Balance"],
    }),
    getTransactions: builder.query({
      query: (obj: any) => {
        if (obj.endDate) {
          return {
            url: `/v1/users/transaction-history`,
            method: "GET",
            params: {
              dateFrom: obj.startDate,
              dateTo: obj.endDate,
            },
          };
        }
        return {
          url: `/v1/users/transaction-history`,
          method: "GET",
          params: {
            dateFrom: obj.params.startDate,
          },
        };
      },
      providesTags: ["PaymentMethods"],
    }),
    addPaymentCard: builder.mutation({
      query: (body) => ({
        url: `/v1/payments/methods/add-card`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    makePayment: builder.mutation({
      query: (body) => ({
        url: `/v1/payments/make-payment`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Balance"],
    }),
    getFeeStructure: builder.mutation({
      query: (amount) => ({
        url: `/v1/payments/fee-structure/${amount}`,
        method: "GET",
      }),
      invalidatesTags: ["Balance"],
    }),
    getAutoPay: builder.query({
      query: () => ({
        url: `/v1/autopay`,
        method: "GET",
      }),
      providesTags: ["AutoPay"],
    }),
    addAutoPay: builder.mutation({
      query: () => ({
        url: `/v1/autopay`,
        method: "PUT",
      }),
      invalidatesTags: ["AutoPay"],
    }),
    updateAutoPay: builder.mutation({
      query: (body) => ({
        url: `/v1/autopay/update`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["AutoPay"],
    }),
    subscribeRentReport: builder.mutation({
      query: (body) => ({
        url: `/v1/credit-reporting/`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Reporting"],
    }),
    reportCreditUnsubscribe: builder.mutation({
      query: () => ({
        url: `/v1/credit-reporting/`,
        method: "PUT",
      }),
      invalidatesTags: ["Reporting"],
    }),
    getReportingStatements: builder.query({
      query: (obj: any) => ({
        url: `/v1/credit-reporting/statement`,
        method: "GET",
        params: {
          startDate: obj.startDate,
          endDate: obj.endDate,
        },
      }),
    }),
    getCreditScore: builder.query({
      query: () => ({
        url: `/v1/credit-reporting/score`,
        method: "GET",
      }),
    }),
    postLeaseApplication: builder.mutation({
      query: (body) => ({
        url: `/v1/lease/application`,
        method: "POST",
        body: body,
      }),
    }),
    postCardPayment: builder.mutation({
      query: (body) => ({
        url: `/v1/payments/card-payment`,
        method: "POST",
        body: body,
      }),
    }),
    postAchPayment: builder.mutation({
      query: (body) => ({
        url: `/v1/payments/ach-payment`,
        method: "POST",
        body: body,
      }),
    }),
    postTicket: builder.mutation({
      query: (body) => ({
        url: `/v1/users/ticket`,
        method: "POST",
        body: body,
      }),
    }),
    getDocs: builder.query({
      query: () => ({
        url: `/v1/lease/docs`,
        method: "GET",
      }),
    }),
    getDoc: builder.query({
      query: (id: string) => ({
        url: `/v1/common/doc/${id}`,
        method: "GET",
      }),
    }),
    getLeaseBalance: builder.query({
      query: (id: string) => ({
        url: `/v1/lease/${id}/balance`,
        method: "GET",
      }),
    }),
    getLeaseDetails: builder.query({
      query: (id: string) => ({
        url: `/v1/lease/${id}`,
        method: "GET",
      }),
    }),
    getRenewalEligibility: builder.query<
      {
        mode: "self_service" | "zoho" | "hidden";
        reason: string | null;
        leaseEndDate: string | null;
        daysRemaining: number | null;
        selfServiceRenewalEnabled: boolean;
        renewalOfferStatus: string | null;
      },
      string
    >({
      query: (leaseId: string) => ({
        url: `/v1/lease/${leaseId}/renewal/eligibility`,
        method: "GET",
      }),
    }),
    getRenewalTerms: builder.query<
      {
        eligible: boolean;
        eligibility: {
          mode: "self_service" | "zoho" | "hidden";
          reason: string | null;
          leaseEndDate: string | null;
          daysRemaining: number | null;
          renewalOfferStatus: string | null;
        };
        offerId: string | null;
        offerStatus: string | null;
        terms: Array<{
          termId: string;
          months: number;
          startDate: string;
          endDate: string;
          monthlyRent: number;
          isPreferred: boolean;
        }>;
        termsReason: string | null;
      },
      string
    >({
      query: (leaseId: string) => ({
        url: `/v1/lease/${leaseId}/renewal/terms`,
        method: "GET",
      }),
    }),
    getMoveInConditionStatus: builder.query<
      MoveInConditionStatusResponse,
      string
    >({
      query: (leaseId: string) => ({
        url: `/v1/lease/${leaseId}/move-in-condition`,
        method: "GET",
      }),
      providesTags: ["MoveInCondition"],
    }),
    submitMoveInConditionForm: builder.mutation<
      MoveInConditionSubmitResponse,
      { leaseId: string; body: MoveInConditionFormSubmitPayload }
    >({
      query: ({ leaseId, body }) => ({
        url: `/v1/lease/${leaseId}/move-in-condition`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Requests", "MoveInCondition"],
    }),
    getPaymentTransactions: builder.query({
      query: () => ({
        url: `/v1/payments/transactions`,
        method: "GET",
      }),
    }),
    getPreSignedUrl: builder.mutation({
      query: (body: {
        fileName: string;
        fileType: string;
        recordType: "WorkOrder";
      }) => ({
        url: `/v1/common/get-presigned-url`,
        method: "POST",
        body,
      }),
    }),
    uploadDocumentLink: builder.mutation({
      query: (body: {
        recordId: string;
        recordType: "WorkOrder";
        fileName: string;
        url: string;
      }) => ({
        url: `/v1/common/upload-document-link`,
        method: "POST",
        body,
      }),
    }),
    getApplications: builder.mutation({
      query: (queryObj: any) => ({
        url: `v1/application?${new URLSearchParams(queryObj).toString()}`,
        method: "GET",
      }),
    }),
    // Auto-fetching query variant used by the homepage Pending Applications
    // card (the mutation above stays for the imperative Settings flow).
    getApplicationsByEmail: builder.query<any[], string>({
      query: (email: string) => ({
        url: `v1/application?${new URLSearchParams({ email }).toString()}`,
        method: "GET",
      }),
    }),
    postApplication: builder.mutation({
      query: (body: any) => ({
        url: `v1/application`,
        method: "POST",
        body: body,
      }),
    }),
    saveApplicationData: builder.mutation({
      query: ({ id, body }: any) => ({
        url: `v1/application/${id}`,
        method: "PUT",
        body: body,
      }),
    }),
    initiateAuthorizationToAssist: builder.mutation<
      {
        externalSubmissionId: string;
        applicationFormId: string;
        residentEmbedSrc: string;
      },
      {
        applicationId: string;
        assisterName: string;
        assisterEmail: string;
        relationship:
          | "property-staff"
          | "caseworker"
          | "family-member"
          | "other";
        relationshipOther?: string;
        reason:
          | "difficulty-writing"
          | "difficulty-understanding"
          | "lep"
          | "other";
        reasonOther?: string;
      }
    >({
      query: ({ applicationId, ...body }) => ({
        url: `v1/application/${applicationId}/authorization-to-assist/initiate`,
        method: "POST",
        body,
      }),
    }),
    getLeaseDepositStatus: builder.query<
      {
        depositHeld: number;
        depositPaid: boolean;
        owesDeposit: boolean;
        unpaid: number;
        enabled: boolean;
      },
      string
    >({
      query: (leaseId) => ({
        url: `v1/lease/${leaseId}/deposit-status`,
        method: "GET",
      }),
    }),
    initiateStandbySession: builder.mutation<
      | {
          owesDeposit: false;
          depositPaid: true;
          depositHeld: number;
          unpaid: number;
          appOrigin: string;
        }
      | {
          owesDeposit: true;
          depositPaid: false;
          depositHeld: number;
          unpaid: number;
          sessionId: string;
          url: string;
          expiresAt: string;
          ready: boolean;
          appOrigin: string;
        },
      {
        leaseId: string;
        /** Non-prod API only: force paid|unpaid without ResMan. */
        depositStatus?: "paid" | "unpaid";
        /** Non-prod API only: skip Standby HTTP, return mock embed URL. */
        mockSession?: boolean;
      }
    >({
      query: ({ leaseId, depositStatus, mockSession }) => ({
        url: `v1/lease/${leaseId}/standby/session`,
        method: "POST",
        body: {
          ...(depositStatus ? { depositStatus } : {}),
          ...(mockSession ? { mockSession: true } : {}),
        },
      }),
    }),
    declineAuthorizationToAssist: builder.mutation<
      { ok: true },
      { applicationId: string }
    >({
      query: ({ applicationId }) => ({
        url: `v1/application/${applicationId}/authorization-to-assist/decline`,
        method: "POST",
      }),
    }),
    submitAffordableApplication: builder.mutation({
      query: ({
        id,
        body,
      }: {
        id: string;
        body?: {
          rent?: number;
          term?: string;
          leaseStart?: string;
          leaseEnd?: string;
          moveInDate?: string;
        };
      }) => ({
        url: `v1/application/${id}/submit-affordable`,
        method: "POST",
        body: body ?? {},
      }),
    }),
    // Live-In Aide sub-flow submit. Persists the aide questionnaire payload onto
    // the aide prop_application.applicationData, flips status to `submitted`, and
    // triggers the lease-forms readiness check (API Task 12).
    submitLiveInAideApplication: builder.mutation<
      { application?: any },
      { applicationId: string; applicationData: unknown }
    >({
      query: ({ applicationId, applicationData }) => ({
        url: `v1/application/affordable/live-in-aide/${applicationId}/submit`,
        method: "POST",
        body: { applicationData },
      }),
    }),
    getApplicationDocumentPresignedUrl: builder.mutation({
      query: ({
        applicationId,
        docKey,
        fileName,
        fileType,
      }: {
        applicationId: string;
        docKey: string;
        fileName: string;
        fileType: string;
      }) => ({
        url: `v1/application/${applicationId}/documents/presigned`,
        method: "POST",
        body: { docKey, fileName, fileType },
      }),
    }),
    confirmApplicationDocumentUpload: builder.mutation({
      query: ({
        applicationId,
        docKey,
        s3Key,
        fileName,
        mime,
        sizeBytes,
        supersede,
      }: {
        applicationId: string;
        docKey: string;
        s3Key: string;
        fileName: string;
        mime?: string;
        sizeBytes?: number;
        supersede?: boolean;
      }) => ({
        url: `v1/application/${applicationId}/documents/confirm`,
        method: "POST",
        body: { docKey, s3Key, fileName, mime, sizeBytes, supersede },
      }),
    }),
    deleteApplicationDocumentUpload: builder.mutation({
      query: ({
        applicationId,
        fileId,
      }: {
        applicationId: string;
        fileId: string;
      }) => ({
        url: `v1/application/${applicationId}/documents/${fileId}`,
        method: "DELETE",
      }),
    }),
    // Upload-time legibility/quality nudge (affordable flow). Called after confirm
    // returns a fileId; the server runs the doc-standards check and returns a
    // non-blocking { status, reasons }. Affordable-gated + feature-flagged server
    // side (returns { status: "skipped" } otherwise). Never blocks upload/submit.
    checkApplicationDocumentLegibility: builder.mutation({
      query: ({
        applicationId,
        fileId,
      }: {
        applicationId: string;
        fileId: string;
      }) => ({
        url: `v1/application/${applicationId}/documents/${fileId}/legibility`,
        method: "POST",
      }),
    }),
    getSesstionDetails: builder.mutation({
      query: (idvId) => ({
        url: `v1/app/idv/session/${idvId}`,
        method: "GET",
      }),
    }),
    getIdvFailureDetails: builder.mutation({
      query: (idvId) => ({
        url: `v1/app/idv/session/${idvId}/failure`,
        method: "GET",
      }),
    }),
    getLinkToken: builder.mutation({
      query: (body) => ({
        url: `v1/app/idv/link/token`,
        method: "POST",
        body,
      }),
    }),
    // Pay-first (ResMan parity): fetch (and server-side freeze) the application
    // fee quote before charging. GET, but exposed as a mutation for imperative
    // use on the payment page (mirrors getApplications).
    getApplicationFee: builder.mutation({
      query: (id: string) => ({
        url: `v1/application/${id}/fee`,
        method: "GET",
      }),
    }),
    // Fix a stale move-in date without admin Save (which would waive the fee).
    updateApplicationMoveInDate: builder.mutation({
      query: ({ id, moveInDate }: { id: string; moveInDate: string }) => ({
        url: `v1/application/${id}/move-in-date`,
        method: "PUT",
        body: { moveInDate },
      }),
    }),
    // Preview marketing rent for a unit/date/term without writing. Used to
    // confirm an updated monthly rent before PUT /selection or /move-in-date.
    getApplicationSelectionQuote: builder.mutation({
      query: ({
        id,
        moveInDate,
        unitNumber,
        term,
      }: {
        id: string;
        moveInDate: string;
        unitNumber?: string;
        term?: string | number;
      }) => ({
        url: `v1/application/${id}/selection-quote`,
        method: "GET",
        params: {
          moveInDate,
          ...(unitNumber ? { unitNumber } : {}),
          ...(term != null && String(term).trim() !== ""
            ? { term: String(term) }
            : {}),
        },
      }),
    }),
    // Swap unit + move-in + term on the same unpaid application (pay-first
    // unit-unavailable recovery). Updates rent; leaves feeQuote amounts intact.
    updateApplicationSelection: builder.mutation({
      query: ({
        id,
        unitNumber,
        moveInDate,
        term,
      }: {
        id: string;
        unitNumber: string;
        moveInDate: string;
        term: string | number;
      }) => ({
        url: `v1/application/${id}/selection`,
        method: "PUT",
        body: { unitNumber, moveInDate, term: String(term) },
      }),
    }),
    // Charge the application fee with the application id as a temporary payer,
    // before any lease exists. Card-only.
    postApplicationPayment: builder.mutation({
      query: (body: any) => ({
        url: `/v1/payments/application-payment`,
        method: "POST",
        body,
      }),
    }),
    // Create the lease after a successful application-fee payment. Idempotent.
    finalizeApplication: builder.mutation({
      query: (id: string) => ({
        url: `v1/application/${id}/finalize`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useResendEmailMutation,
  useResetPasswordEmailMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetSupportContextQuery,
  useCreateUserMutation,
  useUpdateUserAccountMutation,
  useGetRebindLeasesQuery,
  useRebindLeaseMutation,
  useGetWorkOrderCategoriesMutation,
  useCreateWorkOrderMutation,
  useGetWorkOrdersQuery,
  useLazyGetWorkOrdersQuery,
  useGetWorkOrderByIdQuery,
  useGetPaymentMethodsQuery,
  useAddBankAccountMutation,
  useUpdateDefaultBankAccountMutation,
  useDeleteBankAccountMutation,
  useGetBalanceDetailsQuery,
  useLazyGetBalanceDetailsQuery,
  useGetTransactionsQuery,
  useAddPaymentCardMutation,
  useMakePaymentMutation,
  useGetFeeStructureMutation,
  useGetAutoPayQuery,
  useAddAutoPayMutation,
  useUpdateAutoPayMutation,
  useSubscribeRentReportMutation,
  useGetReportingStatementsQuery,
  useGetCreditScoreQuery,
  usePostLeaseApplicationMutation,
  useGetLeaseBalanceQuery,
  useGetLeaseDetailsQuery,
  useGetRenewalEligibilityQuery,
  useGetRenewalTermsQuery,
  useGetMoveInConditionStatusQuery,
  useSubmitMoveInConditionFormMutation,
  usePostCardPaymentMutation,
  usePostAchPaymentMutation,
  usePostTicketMutation,
  useReportCreditUnsubscribeMutation,
  useGetDocsQuery,
  useLazyGetDocQuery,
  useLazyGetPaymentTransactionsQuery,
  useGetWorkOrderCategoriesQQuery,
  useGetPreSignedUrlMutation,
  useUploadDocumentLinkMutation,
  useGetApplicationsMutation,
  useGetApplicationsByEmailQuery,
  usePostApplicationMutation,
  useSaveApplicationDataMutation,
  useInitiateAuthorizationToAssistMutation,
  useGetLeaseDepositStatusQuery,
  useInitiateStandbySessionMutation,
  useDeclineAuthorizationToAssistMutation,
  useSubmitAffordableApplicationMutation,
  useSubmitLiveInAideApplicationMutation,
  useGetApplicationDocumentPresignedUrlMutation,
  useConfirmApplicationDocumentUploadMutation,
  useCheckApplicationDocumentLegibilityMutation,
  useDeleteApplicationDocumentUploadMutation,
  useGetLinkTokenMutation,
  useGetSesstionDetailsMutation,
  useGetIdvFailureDetailsMutation,
  useGetApplicationFeeMutation,
  useUpdateApplicationMoveInDateMutation,
  useGetApplicationSelectionQuoteMutation,
  useUpdateApplicationSelectionMutation,
  usePostApplicationPaymentMutation,
  useFinalizeApplicationMutation,
} = privateApi;
